import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, AlertTriangle } from "lucide-react";
import api from "@/api";
import { useAuth } from "@/context/AuthContext";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [tokenError, setTokenError] = useState(null); // stringa dettaglio errore token

  const submit = async (e) => {
    e.preventDefault();
    if (pw1 !== pw2) {
      toast.error("Le password non coincidono");
      return;
    }
    if (pw1.length < 8) {
      toast.error("La password deve essere lunga almeno 8 caratteri");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw1 });
      toast.success("Password aggiornata. Sei ora connesso.");
      await checkAuth();
      navigate("/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail || "Token non valido o scaduto";
      toast.error(detail, { duration: 8000 });
      // Se il link è "già usato" / "non valido" / "scaduto", suggeriamo l'azione successiva
      const lower = detail.toLowerCase();
      if (lower.includes("già utilizzato") || lower.includes("non valido") || lower.includes("scaduto")) {
        setTokenError(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-md shadow-lg p-8" data-testid="reset-password">
        <div className="overline text-blue-700 mb-2">Reimposta password</div>
        <h1 className="font-display text-2xl font-black mb-2">Scegli una nuova password</h1>
        <p className="text-sm text-slate-600 mb-6">
          Minimo 8 caratteri, con almeno una lettera e un numero.
        </p>

        {tokenError && (
          <div className="mb-4 p-3 bg-rose-50 border border-rose-300 rounded-sm text-xs text-rose-900 flex gap-2" data-testid="reset-token-error">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold mb-1">Link non utilizzabile</div>
              <div className="mb-2">{tokenError}</div>
              <Link to="/forgot-password" className="text-blue-700 hover:underline font-semibold">
                → Richiedi un nuovo link
              </Link>
            </div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Nuova password</label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="password"
                value={pw1}
                onChange={(e) => setPw1(e.target.value)}
                data-testid="reset-pw1"
                className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-sm text-sm"
                required
                autoComplete="new-password"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Ripeti password</label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                data-testid="reset-pw2"
                className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-sm text-sm"
                required
                autoComplete="new-password"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="btn-reset"
            className="w-full py-3 bg-blue-700 hover:bg-blue-800 text-white font-semibold rounded-sm disabled:opacity-60"
          >
            {loading ? "Aggiornamento..." : "Aggiorna password e accedi"}
          </button>
        </form>
      </div>
    </div>
  );
}
